var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { EventEmitter } from 'events';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { ReloadHistoryParams } from '@shared/utils/History/ReloadHistoryParams';
import { SessionInfo } from '@shared/utils/History/SessionInfo';
import { Periods, TFInfo } from '@shared/utils/History/TFInfo';
import { OptionPutCall } from '@shared/utils/Instruments/OptionPutCall';
import { OptionType } from '@shared/utils/Instruments/OptionType';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { Greeks } from './Greeks';
import { IVCalculationPrice } from './IVCalculationPrice';
import { Option, compareOptions } from './Option';
import { OptionTraderUtils } from './OptionTraderUtils';
import { OptionCalculatorModel } from '../OptionCalculator/OptionCalculatorModel';
import { AnalyzerChartDataInputParameters, AnalyzerChartDataPaperPositionInfo } from './OptionAnalyzer/AnalyzerChartDataInputParameters';
import { AnalyzerInfoDataInputParameters } from './OptionAnalyzer/AnalyzerInfoDataInputParameters';
import { OptionAnalyzer } from './OptionAnalyzer/OptionAnalyzer';
import { OptionChain } from './OptionChain/OptionChain';
import { DataCache } from '../../../DataCache';
import { OptionCalculator } from '../OptionCalculator/OptionCalculator';
import { PaperPositionsCache } from './OptionPaperPosition/PaperPositionsCache';
import { OptionVolatilityLab } from './OptionVolatilityLab/OptionVolatilityLab';
import { CashItem } from '../../History/CashItem';
import { SavedOrdersController } from '../../SavedOrders/SavedOrdersController';
import { StrikeInfo, StrikeSeriesInfo, VolatilityLabChartDataInputParameters } from './OptionVolatilityLab/VolatilityLabChartDataInputParameters';
export class OptionTrader {
    get account() {
        return this._account;
    }
    set account(value) {
        this._account = value;
        this.clear(true);
        this.populate();
        this.changeAccountForPaperPositions();
    }
    get fakeOption() {
        return this._fakeOption;
    }
    set fakeOption(value) {
        this.unsubscribe();
        this.clear(false);
        this._fakeOption = value;
        this.subscribe();
        this.eventEmitter.emit('onInstrumentChanged');
        this.populate();
    }
    get underlier() {
        if (isNullOrUndefined(this._fakeOption)) {
            return undefined;
        }
        else {
            return this._fakeOption.ForwardBaseInstrument;
        }
    }
    get seriesDates() {
        const seriesDates = [];
        if (isNullOrUndefined(this.fakeOption)) {
            return seriesDates;
        }
        const instrDateSettingsList = this.fakeOption.InstrDateSettingsList;
        for (let i = 0; i < instrDateSettingsList.length; i++) {
            seriesDates.push(instrDateSettingsList[i].ContractMonthDate);
        }
        return seriesDates;
    }
    get seriesDate() {
        return this._seriesDate;
    }
    set seriesDate(value) {
        this._seriesDate = value;
        this.eventEmitter.emit('onSeriesDateChanged');
    }
    get options() {
        return this.getOptions(this.seriesDate);
    }
    get isDisplayNonStandartContracts() {
        return this._isDisplayNonStandartContracts;
    }
    set isDisplayNonStandartContracts(value) {
        if (this._isDisplayNonStandartContracts === value) {
            return;
        }
        this._isDisplayNonStandartContracts = value;
        this.repopulate();
    }
    constructor() {
        this.eventEmitter = new EventEmitter();
        this.paperPositionsCache = new PaperPositionsCache();
        this._lastProbabilityHistoryPeriodYear = -1;
        this._isDisplayNonStandartContracts = false;
        this.pricingModel = OptionCalculatorModel.BlackSholes;
        this.interestRate = 0;
        this.ivCalculationPrice = IVCalculationPrice.Ask;
        this.optionChain = new OptionChain();
        this.optionAnalyzer = new OptionAnalyzer();
        this.optionVolatilityLab = new OptionVolatilityLab();
        this.raiseOnUpdateMarketPaperPosition = (paperPosition) => {
            if (paperPosition.OrderType !== OrderType.Market) {
                return;
            }
            this.eventEmitter.emit('onUpdatePaperPosition', paperPosition);
        };
        this.newQuote = this.newQuote.bind(this);
        DataCache.OnAddOrder.Subscribe(this.onAddOrder, this);
        DataCache.OnRemoveOrder.Subscribe(this.onRemoveOrder, this);
        DataCache.OnUpdateOrder.Subscribe(this.onUpdateOrder, this);
        DataCache.OnAddPosition.Subscribe(this.onAddPosition, this);
        DataCache.OnRemovePosition.Subscribe(this.onRemovePosition, this);
        DataCache.OnUpdatePosition.Subscribe(this.onUpdatePosition, this);
        DataCache.OnAddSLOrderToPosition.Subscribe(this.onAddSLOrderToPosition, this);
        DataCache.OnAddTPOrderToPosition.Subscribe(this.onAddTPOrderToPosition, this);
    }
    dispose() {
        DataCache.OnAddOrder.UnSubscribe(this.onAddOrder, this);
        DataCache.OnRemoveOrder.UnSubscribe(this.onRemoveOrder, this);
        DataCache.OnUpdateOrder.UnSubscribe(this.onUpdateOrder, this);
        DataCache.OnAddPosition.UnSubscribe(this.onAddPosition, this);
        DataCache.OnRemovePosition.UnSubscribe(this.onRemovePosition, this);
        DataCache.OnUpdatePosition.UnSubscribe(this.onUpdatePosition, this);
        DataCache.OnAddSLOrderToPosition.UnSubscribe(this.onAddSLOrderToPosition, this);
        DataCache.OnAddTPOrderToPosition.UnSubscribe(this.onAddTPOrderToPosition, this);
        this.unsubscribe();
        this.clear(false);
    }
    getUnderlierPrice() {
        const account = this.account;
        const underlier = this.underlier;
        let underlierPrice = NaN;
        if (isNullOrUndefined(account) || isNullOrUndefined(underlier)) {
            return NaN;
        }
        switch (underlier.HistoryType) {
            case HistoryType.QUOTE_LEVEL1:
                underlierPrice = underlier.Level1.GetBid(account);
                break;
            case HistoryType.QUOTE_ASK:
                underlierPrice = underlier.Level1.GetAsk(account);
                break;
            default:
                underlierPrice = underlier.Level1.GetLastPrice(account);
                break;
        }
        if (!isValidNumber(underlierPrice) || underlierPrice <= 0) {
            return NaN;
        }
        else {
            return underlierPrice;
        }
    }
    getOptions(date) {
        const strikePriceSettingsList = this.getStrikePriceSettings(date);
        const options = [];
        const fakeOption = this.fakeOption;
        for (const strikePriceSettings of strikePriceSettingsList) {
            if (this.isDisplayNonStandartContracts === false && isValidString(strikePriceSettings.StrikeVersion)) {
                continue;
            }
            const putInstrument = DataCache.getInstrumentByTradable_ID(strikePriceSettings.InstrumentTradableIDPut, fakeOption.Route);
            const callInstrument = DataCache.getInstrumentByTradable_ID(strikePriceSettings.InstrumentTradableIDCall, fakeOption.Route);
            options.push(new Option(strikePriceSettings.StrikeVersion, strikePriceSettings.StrikePrice, putInstrument, callInstrument, strikePriceSettings.PutEnabled, strikePriceSettings.CallEnabled));
        }
        options.sort(compareOptions);
        return options;
    }
    getCalculator() {
        return OptionCalculator.getCalculator(this.pricingModel);
    }
    getGreeks(putCall, optionInstrument, price = NaN) {
        let greeks = new Greeks();
        if (optionInstrument == null || !optionInstrument.isOptionSymbol) {
            greeks.delta = 1;
            greeks.gamma = 0;
            greeks.vega = 0;
            greeks.theta = 0;
            greeks.rho = 0;
            greeks.iv = 0;
            return greeks;
        }
        const underlierPrice = this.getUnderlierPrice();
        const optionPrice = isNaN(price) ? this.getOptionPrice(optionInstrument) : price;
        const strike = optionInstrument.StrikePrice;
        const daysToExpiration = OptionTraderUtils.getDaysToExpirationForInstrument(optionInstrument) / 365;
        const intRate = this.interestRate;
        if (isNaN(underlierPrice) || isNaN(optionPrice)) {
            return greeks;
        }
        const model = this.getCalculator();
        if (isNullOrUndefined(model)) {
            return greeks;
        }
        greeks = new Greeks();
        greeks.iv = model.iv(putCall, optionPrice, underlierPrice, strike, daysToExpiration, intRate);
        greeks.delta = model.delta(putCall, underlierPrice, strike, daysToExpiration, intRate, greeks.iv);
        greeks.theta = model.theta(putCall, underlierPrice, strike, daysToExpiration, intRate, greeks.iv);
        greeks.rho = model.rho(putCall, underlierPrice, strike, daysToExpiration, intRate, greeks.iv);
        greeks.vega = model.vega(underlierPrice, strike, daysToExpiration, intRate, greeks.iv);
        greeks.gamma = model.gamma(underlierPrice, strike, daysToExpiration, intRate, greeks.iv);
        return greeks;
    }
    getPaperPositions(isApplyNonStandardContractsFilter) {
        const paperPositions = this.paperPositionsCache.getPaperPositions();
        if (isApplyNonStandardContractsFilter && this.isDisplayNonStandartContracts === false) {
            return paperPositions.filter((paperPosition) => paperPosition.isPosition() || isValidString(paperPosition.Instrument.StrikeVersion) === false);
        }
        else {
            return paperPositions;
        }
    }
    getPaperPositionsForAnalyzer(isApplyNonStandardContractsFilter) {
        const paperPositions = this.paperPositionsCache.getPaperPositionsForAnalyzer();
        if (isApplyNonStandardContractsFilter && this.isDisplayNonStandartContracts === false) {
            return paperPositions.filter((paperPosition) => isValidString(paperPosition.Instrument.StrikeVersion) === false);
        }
        else {
            return paperPositions;
        }
    }
    getPaperPosition(option) {
        return this.paperPositionsCache.getPaperPosition(option);
    }
    getPaperPositionForOrder(order) {
        return this.paperPositionsCache.getPaperPositionForOrder(order);
    }
    getPaperPositionForPosition(position) {
        return this.paperPositionsCache.getPaperPositionForPosition(position);
    }
    createPaperPosition(option) {
        const paperPosition = this.paperPositionsCache.createPaperPosition(this.account, option);
        paperPosition.SubscribeOnUpdate(this.raiseOnUpdateMarketPaperPosition);
        this.eventEmitter.emit('onCreatePaperPosition', paperPosition);
    }
    createPaperPositionFromOrderEdit(orderEdit) {
        const paperPosition = this.paperPositionsCache.createPaperPosition(orderEdit.account, orderEdit.instrument);
        SavedOrdersController.updateByOrderEdit(paperPosition, orderEdit);
        this.eventEmitter.emit('onCreatePaperPosition', paperPosition);
    }
    removePaperPosition(option) {
        const paperPosition = this.paperPositionsCache.removePaperPosition(option);
        if (!isNullOrUndefined(paperPosition)) {
            paperPosition.UnsubscribeOnUpdate(this.raiseOnUpdateMarketPaperPosition);
            this.eventEmitter.emit('onRemovePaperPosition', paperPosition);
        }
    }
    removePaperPositionForOrder(order) {
        const paperPosition = this.paperPositionsCache.removePaperPositionForOrder(order);
        if (!isNullOrUndefined(paperPosition)) {
            this.eventEmitter.emit('onRemovePaperPosition', paperPosition);
        }
    }
    removePaperPositionForPosition(position) {
        const paperPosition = this.paperPositionsCache.removePaperPositionForPosition(position);
        if (!isNullOrUndefined(paperPosition)) {
            this.eventEmitter.emit('onRemovePaperPosition', paperPosition);
        }
    }
    updatePaperPosition(option) {
        const paperPosition = this.paperPositionsCache.getPaperPosition(option);
        if (!isNullOrUndefined(paperPosition)) {
            this.eventEmitter.emit('onUpdatePaperPosition', paperPosition);
        }
    }
    changeAccountForPaperPositions() {
        const paperPositions = this.paperPositionsCache.getPaperPositions();
        for (let i = 0; i < paperPositions.length; i++) {
            const paperPosition = paperPositions[i];
            if (paperPosition.isPosition()) {
                continue;
            }
            paperPosition.Account = this.account;
            this.eventEmitter.emit('onCreatePaperPosition', paperPosition);
        }
        this.eventEmitter.emit('onChangeAccountForPaperPositions');
    }
    changeInstrumentForPaperPosition(oldValue, newValue) {
        const paperPosition = this.paperPositionsCache.changeInstrumentForPaperPosition(oldValue, newValue);
        this.eventEmitter.emit('onChangeInstrumentForPaperPosition', paperPosition);
    }
    clearPaperPositions(isApplyNonStandardContractsFilter) {
        const paperPositions = this.getPaperPositions(isApplyNonStandardContractsFilter);
        for (let i = 0; i < paperPositions.length; i++) {
            const paperPosition = paperPositions[i];
            if (paperPosition.isPosition()) {
                continue;
            }
            this.removePaperPosition(paperPosition.Instrument);
        }
    }
    getOptionAnalyzerChartData(chartType = undefined) {
        const underlierPrice = this.getUnderlierPrice();
        const interestRate = this.interestRate;
        const paperPositionsInfo = [];
        const analyzePaperPositions = this.getPaperPositionsForAnalyzer(true);
        for (let i = 0; i < analyzePaperPositions.length; i++) {
            const analyzePaperPosition = analyzePaperPositions[i];
            const greeks = this.getGreeks(analyzePaperPosition.Instrument.PutCall, analyzePaperPosition.Instrument, analyzePaperPosition.PriceForGreeksCalculation);
            paperPositionsInfo.push(new AnalyzerChartDataPaperPositionInfo(analyzePaperPosition, greeks));
        }
        const model = this.getCalculator();
        const inputParams = new AnalyzerChartDataInputParameters(isNullOrUndefined(chartType) ? this.optionAnalyzer.chartType : chartType, underlierPrice, interestRate, paperPositionsInfo, model);
        return this.optionAnalyzer.getOptionAnalyzerChartData(inputParams);
    }
    getOptionAnalyzerInfoData(dataX) {
        const paperPositionsInfo = [];
        const analyzePaperPositions = this.getPaperPositionsForAnalyzer(true);
        for (let i = 0; i < analyzePaperPositions.length; i++) {
            const analyzePaperPosition = analyzePaperPositions[i];
            const greeks = this.getGreeks(analyzePaperPosition.Instrument.PutCall, analyzePaperPosition.Instrument, analyzePaperPosition.PriceForGreeksCalculation);
            paperPositionsInfo.push(new AnalyzerChartDataPaperPositionInfo(analyzePaperPosition, greeks));
        }
        const model = this.getCalculator();
        return this.optionAnalyzer.getOptionAnalyzerInfoData(new AnalyzerInfoDataInputParameters(dataX, this.interestRate, paperPositionsInfo, model));
    }
    getOptionAnalyzerProbabilityDataAsync(leftBorder, rightBorder) {
        return __awaiter(this, void 0, void 0, function* () {
            const account = this.account;
            const instrument = this.underlier;
            const optionAnalyzer = this.optionAnalyzer;
            const lastPrice = instrument.Level1.GetLastPrice(account);
            if (!isValidArray(this._lastProbabilityHistory) ||
                this._lastProbabilityHistoryPeriodYear !== optionAnalyzer.historyPeriodYear) {
                const to = new Date(DateTimeUtils.DateTimeUtcNow().getTime());
                const from = new Date(DateTimeUtils.DateTimeUtcNow().getTime());
                from.setFullYear(from.getFullYear() - optionAnalyzer.historyPeriodYear);
                const tfInfo = new TFInfo();
                tfInfo.Periods = Periods.DAY;
                tfInfo.HistoryType = instrument.HistoryType;
                tfInfo.SessionInfo = new SessionInfo(false);
                tfInfo.Plan = DataCache.GetSpreadPlan(account);
                const historyParams = new ReloadHistoryParams();
                historyParams.updateWithProps({
                    account,
                    instrument,
                    TimeFrameInfo: tfInfo,
                    FromTime: from,
                    ToTime: to,
                    UseDefaultInstrumentHistoryType: false
                });
                const cashItem = yield CashItem.CreateWithHistory(instrument, historyParams);
                if (!isNullOrUndefined(cashItem)) {
                    this._lastProbabilityHistory = cashItem.FNonEmptyCashArray;
                    this._lastProbabilityHistoryPeriodYear = optionAnalyzer.historyPeriodYear;
                }
            }
            return this.optionAnalyzer.getOptionAnalyzerProbabilityData(this._lastProbabilityHistory, lastPrice, leftBorder, rightBorder);
        });
    }
    getVolatilityLabChartData(seriesDates) {
        const underlierPriceStep = this.underlier.PointSize;
        const underlierPricePrecision = this.underlier.Precision;
        const underlierPrice = this.getUnderlierPrice();
        const calculator = this.getCalculator();
        const seriesMapInputParameters = new Map();
        const dates = seriesDates;
        for (const date of dates) {
            const strikes = [];
            const options = this.getOptions(date);
            for (const option of options) {
                const putEnabled = option.putEnabled;
                const callEnabled = option.callEnabled;
                const strikePrice = option.strikePrice;
                const call = option.call;
                const put = option.put;
                const callGreeks = this.getGreeks(OptionPutCall.OPTION_CALL_VANILLA, call);
                const putGreeks = this.getGreeks(OptionPutCall.OPTION_PUT_VANILLA, put);
                const strikeInfo = new StrikeInfo(strikePrice, callEnabled, putEnabled, callGreeks, putGreeks);
                strikes.push(strikeInfo);
            }
            let minATMStrike;
            let maxATMStrike;
            for (let i = 0; i < strikes.length; i++) {
                if (strikes[i].strikePrice <= underlierPrice) {
                    continue;
                }
                if (i === 0) {
                    break;
                }
                minATMStrike = maxATMStrike = strikes[i - 1].strikePrice;
                if (minATMStrike !== underlierPrice) {
                    maxATMStrike = strikes[i].strikePrice;
                }
                break;
            }
            seriesMapInputParameters.set(date, new StrikeSeriesInfo(minATMStrike, maxATMStrike, strikes));
        }
        const inputParameters = new VolatilityLabChartDataInputParameters(underlierPriceStep, underlierPricePrecision, underlierPrice, calculator, seriesMapInputParameters);
        return this.optionVolatilityLab.getVolatilityChartData(inputParameters);
    }
    // #region Subscriptions
    subscribeOnRepopulate(callback) {
        this.eventEmitter.on('onRepopulate', callback);
    }
    unsubscribeOnRepopulate(callback) {
        this.eventEmitter.off('onRepopulate', callback);
    }
    subscribeOnInstrumentChanged(callback) {
        this.eventEmitter.on('onInstrumentChanged', callback);
    }
    unsubscribeOnInstrumentChanged(callback) {
        this.eventEmitter.off('onInstrumentChanged', callback);
    }
    subscribeOnSeriesDateChanged(callback) {
        this.eventEmitter.on('onSeriesDateChanged', callback);
    }
    unsubscribeOnSeriesDateChanged(callback) {
        this.eventEmitter.off('onSeriesDateChanged', callback);
    }
    subscribeOnCreatePaperPosition(callback) {
        this.eventEmitter.on('onCreatePaperPosition', callback);
    }
    unsubscribeOnCreatePaperPosition(callback) {
        this.eventEmitter.off('onCreatePaperPosition', callback);
    }
    subscribeOnUpdatePaperPosition(callback) {
        this.eventEmitter.on('onUpdatePaperPosition', callback);
    }
    unsubscribeOnUpdatePaperPosition(callback) {
        this.eventEmitter.off('onUpdatePaperPosition', callback);
    }
    subscribeOnChangeAccountForPaperPositions(callback) {
        this.eventEmitter.on('onChangeAccountForPaperPositions', callback);
    }
    unsubscribeOnChangeAccountForPaperPositions(callback) {
        this.eventEmitter.off('onChangeAccountForPaperPositions', callback);
    }
    subscribeOnChangeInstrumentForPaperPosition(callback) {
        this.eventEmitter.on('onChangeInstrumentForPaperPosition', callback);
    }
    unsubscribeOnChangeInstrumentForPaperPosition(callback) {
        this.eventEmitter.off('onChangeInstrumentForPaperPosition', callback);
    }
    subscribeOnRemovePaperPosition(callback) {
        this.eventEmitter.on('onRemovePaperPosition', callback);
    }
    unsubscribeOnRemovePaperPosition(callback) {
        this.eventEmitter.off('onRemovePaperPosition', callback);
    }
    subscribeOnQuoteChanged(callback) {
        this.eventEmitter.on('onQuoteChanged', callback);
    }
    unsubscribeOnQuoteChanged(callback) {
        this.eventEmitter.off('onQuoteChanged', callback);
    }
    subscribeOnAddOrder(callback) {
        this.eventEmitter.on('onAddOrder', callback);
    }
    unsubscribeOnAddOrder(callback) {
        this.eventEmitter.off('onAddOrder', callback);
    }
    subscribeOnRemoveOrder(callback) {
        this.eventEmitter.on('onRemoveOrder', callback);
    }
    unsubscribeOnRemoveOrder(callback) {
        this.eventEmitter.off('onRemoveOrder', callback);
    }
    subscribeOnUpdateOrder(callback) {
        this.eventEmitter.on('onUpdateOrder', callback);
    }
    unsubscribeOnUpdateOrder(callback) {
        this.eventEmitter.off('onUpdateOrder', callback);
    }
    subscribeOnAddPosition(callback) {
        this.eventEmitter.on('onAddPosition', callback);
    }
    unsubscribeOnAddPosition(callback) {
        this.eventEmitter.off('onAddPosition', callback);
    }
    subscribeOnRemovePosition(callback) {
        this.eventEmitter.on('onRemovePosition', callback);
    }
    unsubscribeOnRemovePosition(callback) {
        this.eventEmitter.off('onRemovePosition', callback);
    }
    // #endregion
    deserializeOptionAsync(instrumentId, route, contractTradableId) {
        return __awaiter(this, void 0, void 0, function* () {
            const instrument = DataCache.OptionsCache.getOptionFromCacheById(instrumentId, route, contractTradableId);
            if (!isNullOrUndefined(instrument)) {
                return instrument;
            }
            yield DataCache.getOptionById(instrumentId);
            const option = DataCache.OptionsCache.getOptionFromCacheById(instrumentId, route, contractTradableId);
            if (isNullOrUndefined(option)) {
                return DataCache.OptionsCache.getFirstOptionInstrument();
            }
            else {
                return option;
            }
        });
    }
    getOptionAsync(instrument) {
        return __awaiter(this, void 0, void 0, function* () {
            // Copy from NonFixedListCore
            let newIns = DataCache.OptionsCache.getOptionFromCache(instrument);
            if (!isNullOrUndefined(newIns)) {
                return newIns;
            }
            yield DataCache.getOptionById(instrument.Id);
            if (instrument.optionType === OptionType.Fake) {
                newIns = DataCache.OptionsCache.getFirstOption(instrument);
            }
            else {
                newIns = DataCache.OptionsCache.getOptionFromCache(instrument);
            }
            return newIns;
        });
    }
    // #region  Eventhandlers
    newQuote(quote) {
        this.eventEmitter.emit('onQuoteChanged');
    }
    onAddOrder(order) {
        if (!this.IsAllowAccountAndInstrument(order.Account, order.Instrument)) {
            return;
        }
        if (order.isClosingOrder()) {
            const position = DataCache.Trading.getPositionById(order.PositionId);
            if (!isNullOrUndefined(position)) {
                this.onUpdatePosition(position);
            }
        }
        else {
            this.removePaperPositionForOrder(order);
        }
        this.eventEmitter.emit('onAddOrder', order);
    }
    onRemoveOrder(order) {
        if (!this.IsAllowAccountAndInstrument(order.Account, order.Instrument)) {
            return;
        }
        if (order.isClosingOrder()) {
            const position = DataCache.Trading.getPositionById(order.PositionId);
            if (!isNullOrUndefined(position)) {
                this.onUpdatePosition(position);
            }
        }
        this.eventEmitter.emit('onRemoveOrder', order);
    }
    onUpdateOrder(order) {
        // TODO
    }
    onAddPosition(position) {
        if (!this.IsAllowAccountAndInstrument(position.Account, position.Instrument)) {
            return;
        }
        const paperPosition = this.getPaperPositionForPosition(position);
        if (!isNullOrUndefined(paperPosition)) {
            paperPosition.attachPosition(position);
            this.eventEmitter.emit('onUpdatePaperPosition', paperPosition);
        }
        else {
            const paperPosition = this.createPaperPositionForPosition(position);
            this.eventEmitter.emit('onCreatePaperPosition', paperPosition);
        }
        this.eventEmitter.emit('onAddPosition', position);
    }
    onRemovePosition(position) {
        if (!this.IsAllowAccountAndInstrument(position.Account, position.Instrument)) {
            return;
        }
        this.removePaperPositionForPosition(position);
        this.eventEmitter.emit('onRemovePosition', position);
    }
    onUpdatePosition(position) {
        if (!this.IsAllowAccountAndInstrument(position.Account, position.Instrument)) {
            return;
        }
        const paperPosition = this.getPaperPositionForPosition(position);
        if (!isNullOrUndefined(paperPosition)) {
            if (paperPosition.isAttachedPosition(position)) {
                paperPosition.updatePosition();
            }
            else {
                paperPosition.attachPosition(position);
            }
            this.eventEmitter.emit('onUpdatePaperPosition', paperPosition);
        }
        else {
            const paperPosition = this.createPaperPositionForPosition(position);
            this.eventEmitter.emit('onCreatePaperPosition', paperPosition);
        }
        this.eventEmitter.emit('onUpdatePosition', position);
    }
    onAddSLOrderToPosition(position) {
        this.onAddOrder(position.SLOrder);
    }
    onAddTPOrderToPosition(position) {
        this.onAddOrder(position.TPOrder);
    }
    // #endregion
    getStrikePriceSettings(date) {
        if (isNullOrUndefined(this.fakeOption) || isNullOrUndefined(date)) {
            return [];
        }
        const instrDateSettings = this.fakeOption.InstrDateSettingsList.filter((x) => x.ContractMonthDate.getTime() === date.getTime())[0];
        if (!isNullOrUndefined(instrDateSettings)) {
            return [...instrDateSettings.StrikePricesList];
        }
        else {
            return [];
        }
    }
    getOptionPrice(spotInstrument) {
        const account = this.account;
        const ivCalculationPrice = this.ivCalculationPrice;
        let spotPrice = NaN;
        switch (ivCalculationPrice) {
            case IVCalculationPrice.Ask:
                spotPrice = spotInstrument.Level1.GetAsk(account);
                break;
            case IVCalculationPrice.Bid:
                spotPrice = spotInstrument.Level1.GetBid(account);
                break;
            case IVCalculationPrice.Last:
                spotPrice = spotInstrument.Level1.GetLastPrice(account);
                break;
        }
        if (!isValidNumber(spotPrice) || spotPrice <= 0) {
            return NaN;
        }
        else {
            return spotPrice;
        }
    }
    getOrders() {
        const result = [];
        const orders = DataCache.Trading.getAllOrders();
        for (const order of orders) {
            if (this.IsAllowAccountAndInstrument(order.Account, order.Instrument)) {
                result.push(order);
            }
        }
        return result;
    }
    getPositions() {
        const result = [];
        const positions = DataCache.Trading.getAllPositions();
        for (const pos of positions) {
            if (this.IsAllowAccountAndInstrument(pos.Account, pos.Instrument)) {
                result.push(pos);
            }
        }
        return result;
    }
    createPaperPositionForPosition(position) {
        const paperPosition = this.paperPositionsCache.createPaperPosition(this.account, position.Instrument);
        paperPosition.attachPosition(position);
        return paperPosition;
    }
    IsAllowAccountAndInstrument(account, instrument) {
        if (this.account !== account) {
            return false;
        }
        if (isNullOrUndefined(this.underlier)) {
            return false;
        }
        if (this.underlier.InstrumentTradableID === instrument.InstrumentTradableID &&
            this.underlier.Route === instrument.Route) {
            return true;
        }
        if (this.seriesDates.length === 0) {
            return false;
        }
        return this.underlier === instrument.ForwardBaseInstrument;
    }
    populate() {
        if (isNullOrUndefined(this.account) || isNullOrUndefined(this.underlier)) {
            return;
        }
        this.eventEmitter.emit('onRepopulate');
        const positions = this.getPositions();
        for (const position of positions) {
            this.onAddPosition(position);
        }
        const orders = this.getOrders();
        for (const order of orders) {
            this.onAddOrder(order);
        }
    }
    repopulate() {
        this.populate();
        this.eventEmitter.emit('onSeriesDateChanged');
        const paperPositions = this.getPaperPositions(true);
        for (const paperPosition of paperPositions) {
            this.eventEmitter.emit('onCreatePaperPosition', paperPosition);
        }
    }
    clear(onlyPositions) {
        const paperPositions = this.paperPositionsCache.getPaperPositions();
        for (let i = 0; i < paperPositions.length; i++) {
            if (onlyPositions && !paperPositions[i].isPosition()) {
                continue;
            }
            const paperPosition = paperPositions[i];
            paperPosition.UnsubscribeOnUpdate(this.raiseOnUpdateMarketPaperPosition);
        }
        this.paperPositionsCache.clear(onlyPositions);
    }
    subscribe() {
        const underlier = this.underlier;
        if (isNullOrUndefined(underlier)) {
            return;
        }
        DataCache.FQuoteCache.addListener(underlier, this, HistoryType.QUOTE_LEVEL1);
        DataCache.FQuoteCache.addListener(underlier, this, HistoryType.QUOTE_TRADES);
    }
    unsubscribe() {
        const underlier = this.underlier;
        if (isNullOrUndefined(underlier)) {
            return;
        }
        DataCache.FQuoteCache.removeListener(underlier, this, HistoryType.QUOTE_LEVEL1);
        DataCache.FQuoteCache.removeListener(underlier, this, HistoryType.QUOTE_TRADES);
    }
}
